import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import * as api from '../../services/routing/api';
import { useRuntimeConfigQuery } from '../../services/routing/queries';

/** The handful an operator watches while a night is being planned. */
const HEADLINE_PARAMETERS = [
  'routing_enabled',
  'pooling_enabled',
  'max_riders_per_vehicle',
  'max_onboard_pct_of_solo',
  'gap_between_trips',
  'pickup_window_late'
];

const Tile = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <Paper sx={{ p: 1.5 }}>
    <Typography variant="h6" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
      {value}
    </Typography>
    {/* A parameter name is one long word; on a phone the tile is narrower than it is. */}
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
      {label}
    </Typography>
  </Paper>
);

const ENGINES = ['osrm', 'valhalla', 'myle', 'manual'];

// One engine process serves one road graph, so a market that crosses a state line needs the
// engine for each area it reaches into. A comma-separated list, the way the deployment names
// them — this is an ops screen, not an address book.
const AREA_URLS_LABEL = 'myle_area_urls';

const areaUrlList = (raw: string) =>
  raw
    .split(',')
    .map(url => url.trim())
    .filter(Boolean);

const URL_FIELDS = [
  { key: 'osrm_url', label: 'osrm_url' },
  { key: 'osrm_ny_url', label: 'osrm_ny_url' },
  { key: 'valhalla_url', label: 'valhalla_url' },
  { key: 'myle_url', label: 'myle_url' }
] as const;

/** Which layer decided a throughput knob, said the way an operator reads it. */
const sourceLabel = (source: string) =>
  source.startsWith('stored') ? 'stored, overriding the deployment' : 'the deployment default';

export const BaseConfigPanel = () => {
  // Undefined until touched, so the boxes read what is in force: these two are stored settings,
  // not one-shot edits, and a blank box would hide the value the next run is going to use.
  const [workerConcurrency, setWorkerConcurrency] = useState<string>();
  const [maxConcurrentCalls, setMaxConcurrentCalls] = useState<string>();
  const [poolCandidateLimit, setPoolCandidateLimit] = useState('');
  const [poolSimulationLimit, setPoolSimulationLimit] = useState('');
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [engine, setEngine] = useState('');
  const [urlEdits, setUrlEdits] = useState<Record<string, string>>({});
  const [areaEdit, setAreaEdit] = useState<string>();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useRuntimeConfigQuery();

  if (isLoading) return <CircularProgress aria-label="loading config" />;
  if (error) return <Alert severity="error">{(error as Error).message}</Alert>;
  if (!data) return null;

  const { parameters, catalog, overrides, deployment } = data;
  const overridden = new Map(overrides.map(rule => [rule.name, rule.value]));
  const workers = workerConcurrency ?? String(deployment.worker_concurrency);
  const concurrentCalls = maxConcurrentCalls ?? String(deployment.max_concurrent_calls);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['runtime-config'] });

  const valueFor = (name: string) => edits[name] ?? overridden.get(name) ?? '';

  // Only the URLs the operator actually touched are sent: an omitted one leaves that engine
  // alone, where a blank one would drop it back to the deployment URL.
  const changedUrls = () =>
    Object.fromEntries(
      URL_FIELDS.filter(field => urlEdits[field.key] !== undefined).map(field => [
        field.key,
        urlEdits[field.key]
      ])
    );

  const onApply = async () => {
    const body: api.RuntimeConfigUpdate = {};
    const changed = Object.entries(edits)
      .filter(([name, value]) => value !== (overridden.get(name) ?? ''))
      .map(([name, value]) => ({ name, value }));
    if (changed.length) body.parameters = changed;
    if (poolCandidateLimit) body.pool_candidate_limit = Number(poolCandidateLimit);
    if (poolSimulationLimit) body.pool_simulation_limit = Number(poolSimulationLimit);
    if (engine) body.engine = engine;
    const urls: NonNullable<api.RuntimeConfigUpdate['engine_urls']> = changedUrls();
    if (areaEdit !== undefined) urls.myle_area_urls = areaUrlList(areaEdit);
    if (Object.keys(urls).length) body.engine_urls = urls;
    if (!Object.keys(body).length) {
      enqueueSnackbar('Nothing to apply');
      return;
    }
    try {
      await api.putRuntimeConfig(body);
      enqueueSnackbar('Overrides applied — affects runs started from now on');
      setEdits({});
      setPoolCandidateLimit('');
      setPoolSimulationLimit('');
      setEngine('');
      setUrlEdits({});
      refresh();
    } catch (applyError) {
      enqueueSnackbar((applyError as Error).message, { variant: 'error' });
    }
  };

  const onSaveThroughput = async () => {
    try {
      await api.putRuntimeConfig({
        worker_concurrency: Number(workers),
        max_concurrent_calls: Number(concurrentCalls)
      });
      enqueueSnackbar('Throughput saved — kept across restarts, affects the next run');
      // Back to reading the stored value, so the boxes agree with what was just saved.
      setWorkerConcurrency(undefined);
      setMaxConcurrentCalls(undefined);
      refresh();
    } catch (saveError) {
      enqueueSnackbar((saveError as Error).message, { variant: 'error' });
    }
  };

  const onClear = async () => {
    if (
      !window.confirm(
        'Clear ALL runtime overrides, the saved throughput settings included? The next run '
          + 'falls back to the stored parameters and the deployment\u2019s own limits.'
      )
    ) {
      return;
    }
    try {
      await api.clearRuntimeConfig();
      enqueueSnackbar('Overrides cleared');
      setEdits({});
      setEngine('');
      setUrlEdits({});
      setWorkerConcurrency(undefined);
      setMaxConcurrentCalls(undefined);
      setPoolCandidateLimit('');
      setPoolSimulationLimit('');
      refresh();
    } catch (clearError) {
      enqueueSnackbar((clearError as Error).message, { variant: 'error' });
    }
  };

  return (
    <Stack spacing={2} useFlexGap>
      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
          WHAT THE NEXT RUN WILL PLAN WITH
        </Typography>
        <Grid container spacing={1.5} sx={{ mb: 2 }}>
          {HEADLINE_PARAMETERS.map(name => (
            <Grid item xs={6} sm={4} md={2} key={name}>
              <Tile label={name} value={parameters[name]} />
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" color="text.secondary">
          Market-wide values, with no broker, vehicle type or service level narrowing them — a run
          resolves each trip against its own scope, so a scoped rule can differ from these.
          Overrides apply to runs started after the change; in-flight runs keep their snapshot; a
          service restart clears every override except the two throughput settings below.
        </Typography>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
          THROUGHPUT — HOW HARD THIS DEPLOYMENT IS DRIVEN
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How many routes are planned at once, and how many engine calls those workers may have in
          flight. Unlike every other override on this screen, these two are <b>stored</b> — a
          restart keeps them, so a deployment tuned against a real night stays tuned — and a stored
          value outranks what the deployment ships with, so raising the deployed default changes
          nothing until these are cleared. Each field says which one is in force, and what the next
          run will use; change it and save. “Clear all overrides” drops them back to the
          deployment&apos;s.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'flex-start' }} flexWrap="wrap" useFlexGap>
          <TextField
            label="Workers"
            type="number"
            size="small"
            helperText={`routes planned at once — ${sourceLabel(deployment.worker_concurrency_source)}`}
            inputProps={{ min: 1, 'aria-label': 'worker_concurrency' }}
            value={workers}
            onChange={event => setWorkerConcurrency(event.target.value)}
          />
          <TextField
            label="Concurrent engine calls"
            type="number"
            size="small"
            helperText={`engine calls in flight at once — ${sourceLabel(deployment.max_concurrent_calls_source)}`}
            inputProps={{ min: 1, 'aria-label': 'max_concurrent_calls' }}
            value={concurrentCalls}
            onChange={event => setMaxConcurrentCalls(event.target.value)}
          />
          <Button variant="contained" onClick={onSaveThroughput} sx={{ mt: 0.5 }}>
            Save throughput
          </Button>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              RUNTIME PARAMETER OVERRIDES
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              A value here overrides every tenant&apos;s stored rule for runs started from now on.
              Blank it to drop the override and fall back to what is stored.
            </Typography>
            <TableContainer sx={{ maxHeight: 520 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: '50%' }}>Parameter</TableCell>
                    <TableCell sx={{ width: '16%' }}>Effective</TableCell>
                    <TableCell>Override</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {catalog.map(parameter => (
                    <TableRow key={parameter.name}>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        <Typography sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                          {parameter.name} <em>({parameter.unit})</em>
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {parameter.description}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                        {parameters[parameter.name]}
                      </TableCell>
                      <TableCell sx={{ verticalAlign: 'top' }}>
                        {parameter.options ? (
                          <TextField
                            fullWidth
                            select
                            size="small"
                            SelectProps={{
                              displayEmpty: true,
                              SelectDisplayProps: { 'aria-label': parameter.name }
                            }}
                            value={valueFor(parameter.name)}
                            onChange={event =>
                              setEdits(previous => ({
                                ...previous,
                                [parameter.name]: event.target.value
                              }))
                            }
                          >
                            <MenuItem value="">(remove override)</MenuItem>
                            {parameter.options.map(option => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </TextField>
                        ) : (
                          <TextField
                            fullWidth
                            size="small"
                            inputProps={{ 'aria-label': parameter.name }}
                            placeholder={parameter.default}
                            value={valueFor(parameter.name)}
                            onChange={event =>
                              setEdits(previous => ({
                                ...previous,
                                [parameter.name]: event.target.value
                              }))
                            }
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{ mt: 2 }}
              alignItems={{ xs: 'stretch', sm: 'flex-start' }}
              flexWrap="wrap"
              useFlexGap
            >
              <TextField
                label={`pool_candidate_limit (${deployment.pool_candidate_limit})`}
                type="number"
                size="small"
                helperText="candidates kept, nearest first"
                inputProps={{ 'aria-label': 'pool_candidate_limit' }}
                value={poolCandidateLimit}
                onChange={event => setPoolCandidateLimit(event.target.value)}
              />
              <TextField
                label={`pool_simulation_limit (${deployment.pool_simulation_limit})`}
                type="number"
                size="small"
                helperText="of those, how many are simulated"
                inputProps={{ 'aria-label': 'pool_simulation_limit' }}
                value={poolSimulationLimit}
                onChange={event => setPoolSimulationLimit(event.target.value)}
              />
              <Button variant="contained" onClick={onApply} sx={{ mt: { sm: 0.5 } }}>
                Apply overrides
              </Button>
              <Button color="error" onClick={onClear} sx={{ mt: { sm: 0.5 } }}>
                Clear all overrides
              </Button>
            </Stack>
            {overrides.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Active overrides:{' '}
                {overrides.map(rule => `${rule.name}=${rule.value}`).join(', ')}
              </Alert>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                DIRECTIONS ENGINE — {deployment.engine_source}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Which engine the next run drives and where it lives — at a local build, say, or at
                a second instance for a comparison run. A URL that differs from the
                deployment&apos;s keeps its own directions cache, and each run records the URLs it
                planned with.
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Changes here last until the service restarts, and apply to every tenant. To set an
                engine that survives a restart, use the Config tab — it stores the engine per
                tenant, and this panel still wins over it while it is set.
              </Alert>
              <Stack spacing={2}>
                <TextField
                  select
                  size="small"
                  label={`engine (${deployment.engine})`}
                  // A displayEmpty select renders its own text at value '', so the label has to be
                  // told to float or the two are drawn on top of each other.
                  InputLabelProps={{ shrink: true }}
                  SelectProps={{ displayEmpty: true, SelectDisplayProps: { 'aria-label': 'engine' } }}
                  value={engine}
                  onChange={event => setEngine(event.target.value)}
                >
                  <MenuItem value="">(unchanged)</MenuItem>
                  {ENGINES.map(option => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
                {URL_FIELDS.map(field => (
                  <TextField
                    key={field.key}
                    size="small"
                    label={field.label}
                    InputLabelProps={{ shrink: true }}
                    placeholder={deployment.engine_urls[field.key] || '(engine default)'}
                    helperText={
                      urlEdits[field.key] === ''
                        ? 'blank — falls back to the deployment URL'
                        : undefined
                    }
                    inputProps={{ 'aria-label': field.label }}
                    value={urlEdits[field.key] ?? deployment.engine_urls[field.key]}
                    onChange={event =>
                      setUrlEdits(previous => ({ ...previous, [field.key]: event.target.value }))
                    }
                  />
                ))}
                <TextField
                  size="small"
                  label={AREA_URLS_LABEL}
                  InputLabelProps={{ shrink: true }}
                  placeholder={
                    deployment.engine_urls.myle_area_urls.join(', ') || '(none — one area)'
                  }
                  helperText="The engines for the deployment's other areas, comma-separated. Blank
                    means the primary is the only graph, and every stop off it is unroutable."
                  inputProps={{ 'aria-label': AREA_URLS_LABEL }}
                  value={areaEdit ?? deployment.engine_urls.myle_area_urls.join(', ')}
                  onChange={event => setAreaEdit(event.target.value)}
                />
              </Stack>
            </Paper>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                DEPLOYMENT
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Engineering settings, not business parameters: the rest of these change by
                redeploying, never through this screen.
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {Object.entries(deployment)
                      .filter(([key]) => !key.startsWith('engine'))
                      .map(([key, value]) => (
                        <TableRow key={key}>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>{key}</TableCell>
                          <TableCell
                            sx={{ fontFamily: 'monospace', fontSize: 12, overflowWrap: 'anywhere' }}
                          >
                            {String(value)}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default BaseConfigPanel;
